import React, { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, ListGroup, Image, Button, Card, Toast } from 'react-bootstrap'
import Message from '../components/Message'
import Meta from '../components/Meta'
import { normalizeImageUrl } from '../utils/urlSanitizer'
import '../styles/cart.css'
import { addToCart, removeFromCart, updateCartItemQty } from '../slices/cartSlice'
import axios from 'axios'
import { setUserInfo } from '../slices/userSlice'
import { deleteCookie } from '../utils/cookies'
import ReCAPTCHA from 'react-google-recaptcha'
import PolicyConsent from '../components/PolicyConsent'
import FloatingCheckoutButton from '../components/FloatingCheckoutButton'
import GuestCheckoutModal from '../components/GuestCheckoutModal'

// Added currency formatter (Intl.NumberFormat) for consistent 2-decimal USD display
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const formatMoney = (v) => currencyFormatter.format(Number.isFinite(+v) ? +v : 0)
// Future use examples: formatMoney(order.taxPrice), formatMoney(order.shippingPrice)

const CartScreen = ({ match, location, history }) => {
  const productId = match.params.id

  const searchParams = new URLSearchParams(location.search || '')
  const qty = Number(searchParams.get('qty') || 1)
  const selectedColorsVariants = String(searchParams.get('selectedColorsVariants') || '')
  const variantSignature = searchParams.get('variantSignature') || ''

  const dispatch = useDispatch()

  const userInfo = useSelector((state) => state.user.userInfo)


  const cart = useSelector((state) => state.cart)
  const { cartItems } = cart

  const [guestCreating, setGuestCreating] = useState(false)
  const [guestError, setGuestError] = useState(null)
  const creationLatch = useRef(false)
  const [captchaVerifying, setCaptchaVerifying] = useState(false)
  const [captchaToken, setCaptchaToken] = useState(null)
  const recaptchaRef = useRef(null)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)
  const [viewedTerms, setViewedTerms] = useState(false)
  const [viewedPrivacy, setViewedPrivacy] = useState(false)
  const [showGuestModal, setShowGuestModal] = useState(false)

  useEffect(() => {
    if (!productId) return;
    // Guard: if this exact item (by signature or legacy variant) already exists, do not re-add on refresh
    const exists = cartItems.some(it => {
      if (variantSignature) {
        return it.product === productId && it.variantSignature === variantSignature;
      }
      return it.product === productId && it.variantSignature == null && it.selectedColorsVariants === selectedColorsVariants;
    });
    if (exists) {
      try { history.replace('/cart'); } catch {}
      return;
    }
    dispatch(addToCart({ id: productId, qty, selectedColorsVariants, variantSignature }))
      .finally(() => {
        try { history.replace('/cart'); } catch {}
      });
  }, [dispatch, productId, qty, selectedColorsVariants, variantSignature, history, cartItems])

  const removeFromCartHandler = (productId, variantName, variantSignature) => {
    dispatch(removeFromCart({ productId, selectedColorsVariants: variantName, variantSignature }));
  };

  const updateQtyHandler = (productId, variantName, variantSignature, qty) => {
    dispatch(updateCartItemQty({ productId, selectedColorsVariants: variantName, variantSignature, qty }));
  };

  const handleFloatingCheckout = () => {
    if (userInfo && !userInfo.isGuest) {
      history.push('/shipping');
    } else if (userInfo && userInfo.isGuest) {
      history.push('/shipping');
    } else {
      // Anonymous user - open modal
      setShowGuestModal(true);
    }
  };

  const handleModalContinue = (captchaToken) => {
    setCaptchaToken(captchaToken);
    setCaptchaVerifying(true);
    createGuestAfterCaptcha(captchaToken)
      .then(() => {
        setShowGuestModal(false);
      })
      .finally(() => {
        setCaptchaVerifying(false);
      });
  };

  const checkoutHandler = () => {
    if (userInfo && !userInfo.isGuest) {
      history.push('/shipping')
    } else {
      history.push('/login?redirect=shipping')
    }
  }

  const createGuestAfterCaptcha = async (captchaToken) => {
    // moved logic from original checkoutAsGuestHandler, expects captchaToken
    if (userInfo && userInfo.isGuest) {
      history.push('/shipping')
      return
    }
    if (!acceptedTerms || !acceptedPrivacy || !viewedTerms || !viewedPrivacy) {
      setGuestError('You must view and accept the Terms & Conditions and Privacy Policy to continue as guest')
      return
    }
    setGuestCreating(true)
    setGuestError(null)
    let restoreOutcome = 'unknown'
    const attemptRestore = async () => {
      try {
        const { data } = await axios.get('/api/users/session', { withCredentials: true })
        restoreOutcome = 'success'
        dispatch(setUserInfo(data))
        localStorage.setItem('userInfo', JSON.stringify(data))
        setTimeout(() => history.push('/shipping'), 0)
        return true
      } catch (err) {
        const status = err?.response?.status
        if (status === 401 || status === 404) {
          restoreOutcome = 'auth-fail'
          return false
        }
        restoreOutcome = 'network-fail'
        throw err
      }
    }
    let proceedToCreate = false
    try { if (await attemptRestore()) return; proceedToCreate = true } catch { try { if (await attemptRestore()) return; proceedToCreate = true } catch { proceedToCreate = true } }
    if (!proceedToCreate) return
    if (restoreOutcome === 'auth-fail') {
      try { deleteCookie('guestToken'); document.cookie = 'guestToken=; Max-Age=0; path=/' } catch {}
    }
    if (creationLatch.current) return
    creationLatch.current = true
    try {
  const { data: guestData } = await axios.post('/api/users/guest', { grecaptcha: captchaToken, acceptedTerms: true, acceptedPrivacy: true }, { withCredentials: true })
      dispatch(setUserInfo({ ...guestData, isGuest: true }))
      // Never persist the raw guest JWT to localStorage -- the httpOnly
      // guestToken cookie is the real credential; a copy here would be
      // readable by any XSS on the page. Mirrors what normalizeUser
      // already strips before this data reaches Redux state.
      const { token: _guestToken, ...guestDataSafe } = guestData
      localStorage.setItem('userInfo', JSON.stringify({ ...guestDataSafe, isGuest: true }))
      try { await axios.get('/api/users/session') } catch {}
      setTimeout(() => history.push('/shipping'), 0)
    } catch (e) {
      setGuestError(e?.response?.data?.message || (restoreOutcome === 'network-fail' ? 'Guest creation failed after network restore issues' : 'Guest checkout failed'))
      setGuestCreating(false)
      creationLatch.current = false
      recaptchaRef.current && recaptchaRef.current.reset()
      return
    }
    creationLatch.current = false
  }

  const checkoutAsGuestHandler = () => {
    if (userInfo && userInfo.isGuest) { history.push('/shipping'); return }
    if (guestCreating || captchaVerifying) return
    if (!captchaToken) {
      setGuestError('Please complete the captcha')
      return
    }
    if (!acceptedTerms || !acceptedPrivacy || !viewedTerms || !viewedPrivacy) {
      setGuestError('Please view and accept the Terms & Conditions and Privacy Policy')
      return
    }
    setGuestError(null)
    setCaptchaVerifying(true)
    createGuestAfterCaptcha(captchaToken).finally(() => setCaptchaVerifying(false))
  }

  const onGuestCaptcha = (token) => {
    setCaptchaToken(token)
  }

  return (
    <Row>
  <Meta title='Cart — lotustor.com' robots='noindex,follow' />
      <Col md={8}>
        <h1>Shopping Cart</h1>
        {cartItems.length === 0 ? (
          <Message>
            Your cart is empty <Link to='/'>Go Back</Link>
          </Message>
        ) : (
          <ListGroup variant='flush'>
            {cartItems.map((item) => (
              <ListGroup.Item key={`${item.product}::${item.variantSignature || item.selectedColorsVariants || 'NA'}`}>
                <Row className="align-items-center cart-row">
                  <Col xs="auto" md={2}>
                    <Image src={normalizeImageUrl(item.image)} alt={item.name} className="cart-item-thumb rounded" />
                  </Col>
                  <Col md={3}>
                    <Link to={`/product/${item.product}`}>{item.name}</Link>
                  </Col>
                  <Col md={3}>
                    {item.variantSignature ? (
                      <div className='small'>
                        {Array.isArray(item.variantValues) && item.variantValues.length > 0 ? (
                          item.variantValues.map(v => (
                            <div key={v.propId}>{v.propName}: {v.valueName}</div>
                          ))
                        ) : (
                          <div>Variant: {item.variantSignature}</div>
                        )}
                      </div>
                    ) : (
                      <div>Color/Variant: {item.selectedColorsVariants}</div>
                    )}
                  </Col>
                  <Col md={2}>{formatMoney(item.price)}</Col>
                  <Col md={2}>
                    <div className="quantity-selector d-flex align-items-center justify-content-center">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="quantity-btn"
                        onClick={() => {
                          const newQty = Math.max(1, item.qty - 1);
                          if (newQty !== item.qty) {
                            updateQtyHandler(item.product, item.selectedColorsVariants, item.variantSignature, newQty);
                          }
                        }}
                        disabled={item.qty <= 1}
                      >
                        <i className="fas fa-minus"></i>
                      </Button>
                      <span className="quantity-display mx-2">{item.qty}</span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="quantity-btn"
                        onClick={() => {
                          const max = item.countInStock ?? 10;
                          const newQty = Math.min(max, item.qty + 1);
                          if (newQty !== item.qty) {
                            updateQtyHandler(item.product, item.selectedColorsVariants, item.variantSignature, newQty);
                          }
                        }}
                        disabled={item.qty >= (item.countInStock ?? 10)}
                      >
                        <i className="fas fa-plus"></i>
                      </Button>
                    </div>
                  </Col>
                  <Col xs="auto" className="ms-auto d-flex justify-content-end align-items-center cart-action-col">
                    <Button
                      type='button'
                      variant='light'
                      className='cart-delete-btn'
                      onClick={() => removeFromCartHandler(item.product, item.selectedColorsVariants, item.variantSignature)}
                    >
                      <i className='fas fa-trash'></i>
                    </Button>
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Col>
      <Col md={4}>
        <Card className="sticky-cart-summary">
          <ListGroup variant='flush'>
            <ListGroup.Item>
              <h2>
                Subtotal ({cartItems.reduce((acc, item) => acc + Number(item.qty), 0)}) items
              </h2>
              {formatMoney(
                cartItems.reduce((acc, item) => acc + item.qty * item.price, 0)
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <Link to='/'>
                <Button type='button' className='btn-block' style={{ width: '100%' }}>
                  Keep Shopping!
                </Button>
              </Link>
            </ListGroup.Item>

            <ListGroup.Item>
              {userInfo && !userInfo.isGuest && (
                <Button
                  type='button'
                  className='btn-block'
                  style={{ width: '100%' }}
                  disabled={cartItems.length === 0}
                  onClick={checkoutHandler}
                >
                  Proceed To Checkout
                </Button>
              )}
              {userInfo && userInfo.isGuest && (
                <Button
                  type='button'
                  className='btn-block'
                  style={{ width: '100%' }}
                  disabled={cartItems.length === 0}
                  onClick={() => history.push('/shipping')}
                >
                  Continue to Shipping
                </Button>
              )}
              {!userInfo && !guestCreating && (
                <Button
                  type='button'
                  className='btn-block'
                  style={{ width: '100%' }}
                  disabled={cartItems.length === 0}
                  onClick={() => history.push('/login?redirect=shipping')}
                >
                  Sign In
                </Button>
              )}
            </ListGroup.Item>
            {!userInfo && (
              <ListGroup.Item>
                <Button
                  type='button'
                  className='btn-block'
                  style={{ width: '100%' }}
                  onClick={checkoutAsGuestHandler}
                  disabled={cartItems.length === 0 || guestCreating || captchaVerifying || !acceptedTerms || !acceptedPrivacy || !viewedTerms || !viewedPrivacy}
                >
                  {guestCreating ? 'Creating Guest...' : (captchaVerifying ? 'Verifying...' : 'Checkout As Guest')}
                </Button>
                {/* Only show captcha status and widget for anonymous users */}
                {!userInfo && captchaToken && !guestCreating && !captchaVerifying && (
                  <div className='text-success small mt-2'>Captcha verified ✓</div>
                )}
                {!userInfo && (
                  <div className='mt-2'>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                      onChange={onGuestCaptcha}
                    />
                  </div>
                )}
                <div className='mt-2'>
                  <PolicyConsent
                    required
                    initial={{ viewedPrivacy, acceptedPrivacy, viewedTerms, acceptedTerms }}
                    onChange={({ acceptedTerms:at, acceptedPrivacy:ap, viewedTerms:vt, viewedPrivacy:vp }) => {
                      setAcceptedTerms(!!at);
                      setAcceptedPrivacy(!!ap);
                      setViewedTerms(!!vt);
                      setViewedPrivacy(!!vp);
                    }}
                  />
                </div>
              </ListGroup.Item>
            )}
          </ListGroup>
        </Card>
      </Col>
      {/* Toasts */}
      <div
        aria-live='assertive'
        aria-atomic='true'
        style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 1060 }}
      >
        <Toast
          role='alert'
          onClose={() => setGuestError(null)}
            show={!!guestError}
            delay={6000}
            autohide
            className='guest-error-toast'
            style={{
              backgroundColor: '#dc3545',
              color: '#fff',
              border: '1px solid #b02a37',
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
              minWidth: '320px',
              fontSize: '0.95rem'
            }}
        >
          <Toast.Header
            closeButton
            className='guest-error-toast-header'
            style={{
              background: 'rgba(0,0,0,0.15)',
              color: '#fff',
              borderBottom: '1px solid rgba(255,255,255,0.25)'
            }}
          >
            <strong className='me-auto'>⚠️ Guest Checkout Error</strong>
          </Toast.Header>
          <div className='guest-error-toast-body' style={{ padding: '0.75rem 1rem', color: '#fff' }}>
              {guestError}
          </div>
        </Toast>
      </div>

      {/* Floating Checkout Button (Mobile Only) */}
      <FloatingCheckoutButton
        onClick={handleFloatingCheckout}
        disabled={cartItems.length === 0}
        itemCount={cartItems.reduce((acc, item) => acc + item.qty, 0)}
      />

      {/* Guest Checkout Modal */}
      <GuestCheckoutModal
        show={showGuestModal}
        onHide={() => setShowGuestModal(false)}
        onContinue={handleModalContinue}
        loading={guestCreating || captchaVerifying}
        error={guestError}
        captchaToken={captchaToken}
        onCaptchaChange={setCaptchaToken}
        acceptedTerms={acceptedTerms}
        acceptedPrivacy={acceptedPrivacy}
        viewedTerms={viewedTerms}
        viewedPrivacy={viewedPrivacy}
        onPolicyChange={({ acceptedTerms: at, acceptedPrivacy: ap, viewedTerms: vt, viewedPrivacy: vp }) => {
          setAcceptedTerms(!!at);
          setAcceptedPrivacy(!!ap);
          setViewedTerms(!!vt);
          setViewedPrivacy(!!vp);
        }}
      />
    </Row>
  )
}

export default CartScreen

// import React, { useEffect } from 'react'
// import { Link } from 'react-router-dom'
// import { useDispatch, useSelector } from 'react-redux'
// import { Row, Col, ListGroup, Image, Form, Button, Card } from 'react-bootstrap'
// import Message from '../components/Message'
// import { addToCart, removeFromCart } from '../actions/cartActions'
// import { createGuest } from '../actions/userActions'

// const CartScreen = ({ match, location, history }) => {
//   const productId = match.params.id

//   const qtyParam = location.search ? location.search.split('qty=')[1] : '';
//   const qty = qtyParam ? Number(qtyParam.split('&')[0]) : 1;
//   const selectedColorsVariants = location.search ? String(location.search.split('selectedColorsVariants=')[1]) : '';
//   const dispatch = useDispatch()

//   const userLogin = useSelector((state) => state.userLogin)
//   const { userInfo } = userLogin

//   const cart = useSelector((state) => state.cart)
//   const { cartItems } = cart

//   useEffect(() => {
//     if (productId) {
//       dispatch(addToCart(productId, qty, selectedColorsVariants))
//     }
//   }, [dispatch, productId, qty, selectedColorsVariants])

//   const removeFromCartHandler = (id) => {
//     dispatch(removeFromCart(id))
//   }

//   const checkoutHandler = () => {
//     history.push('/login?redirect=shipping')
//   }

//   const checkoutAsGuest = async () => {
//     await dispatch(createGuest())
//     history.push('/shipping')
//   }

//   return (
//     <Row>
//       <Col md={8}>
//         <h1>Shopping Cart</h1>
//         {cartItems.length === 0 ? (
//           <Message>
//             Your cart is empty <Link to='/'>Go Back</Link>
//           </Message>
//         ) : (
//           <ListGroup variant='flush'>
//             {cartItems.map((item) => (
//               <ListGroup.Item key={item.product}>
//                 <Row>
//                   <Col md={2}>
//                     <Image src={item.image} alt={item.name} fluid rounded />
//                   </Col>
//                   <Col md={3}>
//                     <Link to={`/product/${item.product}`}>{item.name}</Link>
//                   </Col>
//                   <Col md={2}>Color/Variant: {item.selectedColorsVariants}</Col>
//                   <Col md={2}>${item.price}</Col>
//                   <Col md={2}>
//                     <Form.Control
//                       as='select'
//                       value={item.qty}
//                       onChange={(e) =>
//                         dispatch(
//                           addToCart(item.product, Number(e.target.value))
//                         )
//                       }
//                     >
//                       {[...Array(item.countInStock).keys()].map((x) => (
//                         <option key={x + 1} value={x + 1}>
//                           {x + 1}
//                         </option>
//                       ))}
//                     </Form.Control>
//                   </Col>
//                   <Col md={2}>
//                     <Button
//                       type='button'
//                       variant='light'
//                       onClick={() => removeFromCartHandler(item.product)}
//                     >
//                       <i className='fas fa-trash'></i>
//                     </Button>
//                   </Col>
//                 </Row>
//               </ListGroup.Item>
//             ))}
//           </ListGroup>
//         )}
//       </Col>
//       <Col md={4}>
//         <Card>
//           <ListGroup variant='flush'>
//             <ListGroup.Item>
//               <h2>
//                 Subtotal ({cartItems.reduce((acc, item) => acc + item.qty, 0)})
//                 items
//               </h2>
//               $
//               {cartItems
//                 .reduce((acc, item) => acc + item.qty * item.price, 0)
//                 .toFixed(2)}
//             </ListGroup.Item>
//             <ListGroup.Item>
            
//                 <Link to='/'> <Button
//                 type='button'
//                 className='btn-block'
//                 style={{width: 100+"%" }}
//               > Keep Shopping!</Button></Link>
              
//             </ListGroup.Item>
//             <ListGroup.Item>
            
//               <Button
//                 type='button'
//                 className='btn-block'
//                 style={{width: 100+"%" }}
//                 disabled={cartItems.length === 0}
//                 onClick={checkoutHandler}
//               >
//                 Proceed To Checkout
//               </Button>
//             </ListGroup.Item>
//           </ListGroup>

//           {!userInfo && (
//                   <ListGroup.Item>
//                     <Button
//                       type='button'
//                       className='btn btn-block'
//                       style={{width: 100+"%" }}
//                       onClick={''}
//                     >
//                       Checkout As Guest
//                     </Button>
//                   </ListGroup.Item>
//                 )}
//         </Card>
//       </Col>
//     </Row>
//   )
// }

// export default CartScreen
